// Synthesis toolkit for the sound bank (bank.ts) and the music instruments (music/instruments.ts): voices drawn into
// any audio context, live or offline. Each voice has a click-free attack, an optional hold, a natural exponential
// release, optional filter sweeps and a stereo position. A sound designer's kit: modal bells with real bell partials,
// FM, supersaws, plucked strings (Karplus-Strong), filtered noise with a moving pan, pitched thumps, struck metal, a
// soft saturator.

export interface Kit { ac: BaseAudioContext; out: AudioNode; noise: AudioBuffer }

interface Env {
  at: number;
  /** Length (s) from the start to silence, the release included. */
  dur: number;
  vol: number;
  attack?: number;
  /** Seconds at full level after the attack, before the release (0: the sound decays from its peak). */
  hold?: number;
  pan?: number;
}
export interface ToneOpts extends Env { f: number; to?: number; type?: OscillatorType; lp?: number; lpTo?: number; q?: number; detune?: number; glide?: number }
export interface FmOpts extends Env { f: number; ratio: number; index: number; to?: number; detune?: number }
export interface NoiseOpts extends Env { filter?: BiquadFilterType; f: number; to?: number; q?: number; panTo?: number }
export interface SawOpts extends Env { f: number; voices?: number; spread?: number; lp?: number; lpTo?: number; q?: number; width?: number }
export interface BellOpts extends Env {
  f: number;
  /** 0 (dark) to 1 (bright): how much the upper partials ring. */
  bright?: number;
  /** Partial set: a church bell or a small glass. */
  kind?: 'bell' | 'glass';
}
export interface PluckOpts extends Env {
  f: number;
  /** 0 (muted, nylon) to 1 (bright, steel). */
  bright?: number;
}

/**
 * Voice output: a gain with an envelope, panned, into the kit's output. Attack, optional hold, then an exponential
 * approach to silence: a time constant of a fifth of the release reaches -43 dB at its end.
 */
function voice(k: Kit, e: Env): GainNode {
  const g = k.ac.createGain(), t = e.at, a = Math.max(0.001, e.attack ?? 0.004), hold = Math.max(0, Math.min(e.hold ?? 0, e.dur - a));
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(e.vol, t + a);
  if (hold > 0) g.gain.setValueAtTime(e.vol, t + a + hold);
  g.gain.setTargetAtTime(0, t + a + hold, Math.max(0.004, (e.dur - a - hold) / 5));
  if (e.pan) { const p = k.ac.createStereoPanner(); p.pan.value = Math.max(-1, Math.min(1, e.pan)); g.connect(p).connect(k.out); } else g.connect(k.out);
  return g;
}

const filter = (k: Kit, type: BiquadFilterType, f: number, q: number) => { const x = k.ac.createBiquadFilter(); x.type = type; x.frequency.value = f; x.Q.value = q; return x; };
/** Frequencies stay audible and under Nyquist whatever a pitch computation produces. */
const clampF = (k: Kit, f: number) => Math.max(1, Math.min(k.ac.sampleRate / 2 - 1, Number.isFinite(f) ? f : 1));

/** An oscillator, optionally detuned in two layers (warmth), with a pitch glide and a filter sweep. */
export function tone(k: Kit, o: ToneOpts) {
  const g = voice(k, o), t = o.at, end = t + o.dur, layers = o.detune ? [-o.detune, o.detune] : [0];
  let head: AudioNode = g;
  if (o.lp) {
    const f = filter(k, 'lowpass', clampF(k, o.lp), o.q ?? 0.7);
    if (o.lpTo) f.frequency.exponentialRampToValueAtTime(clampF(k, o.lpTo), end);
    f.connect(g); head = f;
  }
  for (const c of layers) {
    const osc = k.ac.createOscillator(), lg = k.ac.createGain();
    osc.type = o.type ?? 'triangle'; osc.detune.value = c; lg.gain.value = 1 / Math.sqrt(layers.length);
    osc.frequency.setValueAtTime(clampF(k, o.f), t);
    if (o.to) osc.frequency.exponentialRampToValueAtTime(clampF(k, o.to), t + (o.glide ?? o.dur));
    osc.connect(lg).connect(head); osc.start(t); osc.stop(end + 0.05);
  }
}

/**
 * FM: a sine carrier modulated by another sine at `ratio` times its pitch, the modulation fading faster than the
 * note. Integer ratios ring like chimes and electric pianos, irrational ones like struck metal.
 */
export function fm(k: Kit, o: FmOpts) {
  const g = voice(k, o), t = o.at, end = t + o.dur;
  for (const c of o.detune ? [-o.detune, o.detune] : [0]) {
    const car = k.ac.createOscillator(), mod = k.ac.createOscillator(), depth = k.ac.createGain(), lg = k.ac.createGain();
    car.type = 'sine'; mod.type = 'sine'; car.detune.value = c; mod.detune.value = c;
    car.frequency.setValueAtTime(clampF(k, o.f), t); mod.frequency.setValueAtTime(clampF(k, o.f * o.ratio), t);
    if (o.to) { car.frequency.exponentialRampToValueAtTime(clampF(k, o.to), end); mod.frequency.exponentialRampToValueAtTime(clampF(k, o.to * o.ratio), end); }
    depth.gain.setValueAtTime(o.f * o.index, t);
    depth.gain.setTargetAtTime(o.f * o.index * 0.08, t + 0.005, o.dur / 7);
    lg.gain.value = o.detune ? 0.7 : 1;
    mod.connect(depth).connect(car.frequency); car.connect(lg).connect(g);
    car.start(t); mod.start(t); car.stop(end + 0.05); mod.stop(end + 0.05);
  }
}

/** Filtered noise with a cutoff sweep and an optional pan sweep (a pass-by): air, whooshes, impacts, crowds. */
export function noise(k: Kit, o: NoiseOpts) {
  const g = voice(k, o.panTo === undefined ? o : { ...o, pan: 0 }), t = o.at, src = k.ac.createBufferSource(), f = filter(k, o.filter ?? 'lowpass', clampF(k, o.f), o.q ?? 0.8);
  src.buffer = k.noise; src.loop = true;
  if (o.to) f.frequency.exponentialRampToValueAtTime(clampF(k, o.to), t + o.dur);
  if (o.panTo !== undefined) {
    // a moving sound: its own panner sweeps from `pan` to `panTo` over the length, before the voice gain
    const p = k.ac.createStereoPanner();
    p.pan.setValueAtTime(Math.max(-1, Math.min(1, o.pan ?? 0)), t);
    p.pan.linearRampToValueAtTime(Math.max(-1, Math.min(1, o.panTo)), t + o.dur);
    src.connect(f).connect(p).connect(g);
  } else src.connect(f).connect(g);
  src.start(t, Math.random() * 1.5); src.stop(t + o.dur + 0.05);
}

/** A pitched thump: the body of an impact, a kick drum's sine dropping fast in pitch. */
export function thump(k: Kit, at: number, f: number, to: number, dur: number, vol: number, pan = 0) {
  tone(k, { at, dur, vol, pan, f, to, type: 'sine', glide: dur * 0.5, attack: 0.002 });
}

/** A transient: a few milliseconds of bright noise that gives a sound its edge. */
export function click(k: Kit, at: number, f: number, vol: number, pan = 0) {
  noise(k, { at, dur: 0.012, vol, pan, filter: 'bandpass', f, q: 1.2, attack: 0.0008 });
}

/** Partials of struck metal: [frequency ratio, amplitude]. */
export type Partials = readonly (readonly [number, number])[];
const METAL: Partials = [[1, 1], [2.76, 0.5], [5.4, 0.28], [8.93, 0.14]];
/** A cowbell's two main modes and their overtones. */
export const COWBELL: Partials = [[1, 1], [1.5, 0.8], [2.4, 0.3], [3.3, 0.18], [4.1, 0.08]];

/** Struck metal: inharmonic partials decaying at different rates (a clang, a cowbell, a bike frame). */
export function metal(k: Kit, at: number, f: number, dur: number, vol: number, pan = 0, parts: Partials = METAL) {
  for (const [r, a] of parts) {
    if (f * r >= k.ac.sampleRate / 2) continue;
    tone(k, { at, dur: dur / Math.sqrt(r), vol: vol * a, f: f * r, type: 'sine', pan, attack: 0.001 });
  }
}

/** [frequency ratio, amplitude, share of the length it rings] of a bell's partials: hum, prime, tierce, quint, nominal, the upper ring. */
const BELL: readonly (readonly [number, number, number])[] = [[0.5, 0.35, 1], [1, 1, 0.9], [1.183, 0.55, 0.65], [1.506, 0.4, 0.55], [2, 0.5, 0.45], [2.514, 0.22, 0.3], [2.662, 0.14, 0.28], [3.011, 0.12, 0.22], [4.166, 0.06, 0.15]];
const GLASS: readonly (readonly [number, number, number])[] = [[1, 1, 1], [2.32, 0.4, 0.5], [4.25, 0.18, 0.3], [6.63, 0.07, 0.18]];

/**
 * A modal bell: the partials of a real bell, each one slightly detuned against itself so the note beats and breathes,
 * the high partials dying first, struck by a short clapper transient. `glass` rings shorter and purer.
 */
export function bell(k: Kit, o: BellOpts) {
  const bright = Math.max(0, Math.min(1, o.bright ?? 0.6)), parts = o.kind === 'glass' ? GLASS : BELL;
  for (const [r, a, life] of parts) {
    const f = o.f * r, amp = a * (r > 1.6 ? 0.45 + 0.55 * bright : 1);
    if (amp < 0.004 || f >= k.ac.sampleRate / 2) continue;
    tone(k, { at: o.at, dur: Math.max(0.05, o.dur * life), vol: o.vol * amp, pan: o.pan, f, type: 'sine', attack: o.attack ?? 0.002, hold: o.hold, detune: 2.5 });
  }
  // the clapper: a short burst of bright noise, so the bell is struck rather than switched on
  noise(k, { at: o.at, dur: 0.02, vol: o.vol * (0.12 + 0.2 * bright), pan: o.pan, filter: 'bandpass', f: o.f * 3, q: 1.5, attack: 0.0006 });
}

/** Detuned sawtooth voices spread across the stereo field, through a sweeping low-pass: pads, leads, risers. */
export function supersaw(k: Kit, o: SawOpts) {
  const g = voice(k, { ...o, pan: 0 }), t = o.at, end = t + o.dur, n = Math.max(1, Math.min(9, o.voices ?? 5)), spread = o.spread ?? 10, width = o.width ?? 0.6;
  const f = filter(k, 'lowpass', clampF(k, o.lp ?? 4000), o.q ?? 0.8);
  if (o.lpTo) f.frequency.exponentialRampToValueAtTime(clampF(k, o.lpTo), end);
  f.connect(g);
  for (let i = 0; i < n; i++) {
    const x = n === 1 ? 0 : (i / (n - 1)) * 2 - 1, osc = k.ac.createOscillator(), lg = k.ac.createGain(), p = k.ac.createStereoPanner();
    osc.type = 'sawtooth'; osc.frequency.setValueAtTime(clampF(k, o.f), t); osc.detune.value = x * spread;
    lg.gain.value = (x === 0 ? 1 : 0.8) / Math.sqrt(n); p.pan.value = Math.max(-1, Math.min(1, x * width + (o.pan ?? 0)));
    osc.connect(lg).connect(p).connect(f); osc.start(t); osc.stop(end + 0.05);
  }
}

/**
 * A plucked string: its harmonics, each dying faster the higher it is (as on a real string, where the stiffness and
 * the air take the highs first), the odd ones a touch louder, with the pick's own short burst of noise at the start.
 * Additive rather than a feedback delay: a feedback loop never leaves a WebAudio graph, and a race plucks hundreds.
 * Brightness sets how many harmonics speak and how long they live.
 */
export function pluck(k: Kit, o: PluckOpts) {
  const t = o.at, bright = Math.max(0, Math.min(1, o.bright ?? 0.6)), f = clampF(k, o.f), n = 4 + Math.round(4 * bright);
  for (let h = 1; h <= n; h++) {
    const fh = f * h;
    if (fh >= k.ac.sampleRate / 2) break;
    const amp = Math.pow(h, -(1.6 - 0.7 * bright)) * (h % 2 ? 1 : 0.8), life = o.dur * Math.pow(h, -(0.55 - 0.25 * bright));
    tone(k, { at: t, dur: Math.max(0.03, life), vol: o.vol * amp * 0.6, f: fh, type: 'sine', pan: o.pan, attack: o.attack ?? 0.0015, detune: h === 1 ? 1.5 : 0 });
  }
  noise(k, { at: t, dur: 0.012 + 0.01 * bright, vol: o.vol * (0.15 + 0.25 * bright), pan: o.pan, filter: 'bandpass', f: clampF(k, f * (3 + 3 * bright)), q: 1.2, attack: 0.0006 });
}

/** A soft saturator in front of the output: drives impacts into a warm, dense crunch instead of a thin click. */
export function saturate(k: Kit, drive: number): Kit {
  const ws = k.ac.createWaveShaper(), n = 1024, curve = new Float32Array(n);
  for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; curve[i] = Math.tanh(x * drive) / Math.tanh(drive); }
  ws.curve = curve; ws.oversample = '2x';
  ws.connect(k.out);
  return { ...k, out: ws };
}

/** Notes by name (equal temperament, A4 = 440 Hz), so the stings share one musical key. */
export function note(name: string): number {
  const m = /^([A-G])(#|b)?(\d)$/.exec(name);
  if (!m) throw new Error(`bad note ${name}`);
  const semis: Record<string, number> = { C: -9, D: -7, E: -5, F: -4, G: -2, A: 0, B: 2 };
  const n = semis[m[1]] + (m[2] === '#' ? 1 : m[2] === 'b' ? -1 : 0) + (Number(m[3]) - 4) * 12;
  return 440 * Math.pow(2, n / 12);
}

/** Frequency of a MIDI note number (69 = A4). */
export const midi = (n: number): number => 440 * Math.pow(2, (n - 69) / 12);
